import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, X } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/admin/ImageUpload";
import { api } from "@/lib/api";

const inputClass =
  "w-full bg-surface border border-line px-4 py-3 text-sm text-bone placeholder:text-fog/40 focus:border-gold focus:outline-none transition-colors duration-300";

const EMPTY = {
  slug: "",
  title: "",
  client: "",
  category: "product",
  year: String(new Date().getFullYear()),
  services: [],
  hero: "",
  excerpt: "",
  context: "",
  challenge: "",
  approach: "",
  outcome: "",
  metrics: [{ value: "", label: "" }],
  gallery: [],
  video: "",
  before_image: "",
  after_image: "",
};

const slugify = (text) =>
  text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-2">{label}</label>
    {children}
  </div>
);

const ProjectEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !slug;
  const initialCategory = ["product", "architecture", "food", "motion-ai"].includes(searchParams.get("categoria"))
    ? searchParams.get("categoria")
    : "product";
  const [form, setForm] = useState({ ...EMPTY, category: initialCategory });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/projects/${slug}`)
      .then((res) => setForm({ ...EMPTY, ...res.data, video: res.data.video || "" }))
      .catch(() => toast.error("Progetto non trovato"))
      .finally(() => setLoading(false));
  }, [slug, isNew]);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const setValue = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      services: typeof form.services === "string" ? form.services.split(",").map((s) => s.trim()).filter(Boolean) : form.services,
      metrics: form.metrics.filter((m) => m.value || m.label),
      gallery: form.gallery.filter(Boolean),
      video: form.video || null,
      before_image: form.before_image || null,
      after_image: form.after_image || null,
    };
    if (!payload.slug) {
      toast.error("Inserisci almeno il titolo");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/projects", payload);
      } else {
        await api.put(`/admin/projects/${slug}`, payload);
      }
      toast.success("Progetto salvato");
      navigate("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-xs font-mono tracking-[0.25em] text-fog">CARICAMENTO…</p>;

  return (
    <div data-testid="admin-project-editor">
      <Link to="/admin" data-testid="editor-back-link" className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.25em] text-fog hover:text-gold transition-colors duration-300">
        <ArrowLeft size={12} /> TORNA AI PROGETTI
      </Link>
      <h1 className="mt-4 text-2xl font-normal tracking-tight text-bone">
        {isNew ? "Nuovo progetto" : `Modifica — ${form.title}`}
      </h1>

      <form onSubmit={handleSave} className="mt-8 space-y-8">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Titolo *">
            <input data-testid="project-title-input" required value={form.title} onChange={set("title")} className={inputClass} />
          </Field>
          <Field label="Slug (URL)">
            <input data-testid="project-slug-input" value={form.slug} onChange={set("slug")} placeholder="generato dal titolo" className={inputClass} />
          </Field>
          <Field label="Cliente">
            <input data-testid="project-client-input" value={form.client} onChange={set("client")} className={inputClass} />
          </Field>
          <Field label="Anno">
            <input data-testid="project-year-input" value={form.year} onChange={set("year")} className={inputClass} />
          </Field>
          <Field label="Categoria *">
            <select data-testid="project-category-select" value={form.category} onChange={set("category")} className={inputClass}>
              <option value="product">Product</option>
              <option value="architecture">Architecture</option>
              <option value="food">Food</option>
              <option value="motion-ai">Motion / AI / Video</option>
            </select>
          </Field>
          <Field label="Servizi (separati da virgola)">
            <input
              data-testid="project-services-input"
              value={Array.isArray(form.services) ? form.services.join(", ") : form.services}
              onChange={set("services")}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label="Breve descrizione (appare negli elenchi)">
          <textarea data-testid="project-excerpt-input" rows={2} value={form.excerpt} onChange={set("excerpt")} className={`${inputClass} resize-none`} />
        </Field>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Contesto">
            <textarea data-testid="project-context-input" rows={4} value={form.context} onChange={set("context")} className={`${inputClass} resize-none`} />
          </Field>
          <Field label="La sfida">
            <textarea data-testid="project-challenge-input" rows={4} value={form.challenge} onChange={set("challenge")} className={`${inputClass} resize-none`} />
          </Field>
          <Field label="Approccio visivo">
            <textarea data-testid="project-approach-input" rows={4} value={form.approach} onChange={set("approach")} className={`${inputClass} resize-none`} />
          </Field>
          <Field label="Risultato">
            <textarea data-testid="project-outcome-input" rows={4} value={form.outcome} onChange={set("outcome")} className={`${inputClass} resize-none`} />
          </Field>
        </div>

        <div>
          <p className="text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-3">Metriche risultato (numero + etichetta)</p>
          <div className="space-y-2">
            {form.metrics.map((metric, i) => (
              <div key={i} className="flex gap-2">
                <input
                  data-testid={`metric-value-${i}`}
                  value={metric.value}
                  onChange={(e) => setForm((prev) => ({ ...prev, metrics: prev.metrics.map((m, j) => (j === i ? { ...m, value: e.target.value } : m)) }))}
                  placeholder="24+"
                  className={`${inputClass} w-32`}
                />
                <input
                  data-testid={`metric-label-${i}`}
                  value={metric.label}
                  onChange={(e) => setForm((prev) => ({ ...prev, metrics: prev.metrics.map((m, j) => (j === i ? { ...m, label: e.target.value } : m)) }))}
                  placeholder="Immagini coordinate"
                  className={inputClass}
                />
                <button
                  type="button"
                  data-testid={`metric-remove-${i}`}
                  onClick={() => setForm((prev) => ({ ...prev, metrics: prev.metrics.filter((_, j) => j !== i) }))}
                  className="px-3 border border-line text-fog hover:text-red-400 hover:border-red-400 transition-[color,border-color] duration-300"
                  aria-label="Rimuovi metrica"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            data-testid="metric-add-button"
            onClick={() => setForm((prev) => ({ ...prev, metrics: [...prev.metrics, { value: "", label: "" }] }))}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 border border-line text-fog text-[10px] tracking-[0.15em] hover:text-bone hover:border-fog transition-[color,border-color] duration-300"
          >
            <Plus size={12} /> AGGIUNGI METRICA
          </button>
        </div>

        <ImageUpload label="Immagine hero (copertina del progetto)" value={form.hero} onChange={setValue("hero")} testId="project-hero-upload" />

        <div>
          <p className="text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-3">Galleria immagini</p>
          <p className="text-xs text-fog/70 leading-relaxed mb-4 max-w-lg">Le prime 3 foto occupano le posizioni principali del layout (larga dopo l'introduzione, accanto al testo, finale a tutta larghezza). Tutte le altre che aggiungi compaiono in ordine in una griglia in fondo alla pagina del progetto.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {form.gallery.map((url, i) => (
              <ImageUpload
                key={i}
                label={`Immagine ${i + 1}`}
                value={url}
                onChange={(value) => setForm((prev) => ({ ...prev, gallery: prev.gallery.map((g, j) => (j === i ? value : g)).filter((g, j) => !(j === i && !value)) }))}
                testId={`gallery-upload-${i}`}
              />
            ))}
          </div>
          <button
            type="button"
            data-testid="gallery-add-button"
            onClick={() => setForm((prev) => ({ ...prev, gallery: [...prev.gallery, ""] }))}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 border border-line text-fog text-[10px] tracking-[0.15em] hover:text-bone hover:border-fog transition-[color,border-color] duration-300"
          >
            <Plus size={12} /> AGGIUNGI IMMAGINE
          </button>
        </div>

        <ImageUpload label="Video del progetto (opzionale — link YouTube/Vimeo o file MP4)" value={form.video} onChange={setValue("video")} testId="project-video-upload" />

        <div>
          <p className="text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-3">Slider Prima / Dopo (carica entrambe per attivarlo nella case study)</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <ImageUpload label="Immagine PRIMA (grezza)" value={form.before_image} onChange={setValue("before_image")} testId="project-before-upload" />
            <ImageUpload label="Immagine DOPO (finale)" value={form.after_image} onChange={setValue("after_image")} testId="project-after-upload" />
          </div>
        </div>

        <button
          type="submit"
          data-testid="project-save-button"
          disabled={saving}
          className="px-10 py-4 bg-gold text-night text-xs font-semibold tracking-[0.2em] hover:bg-bone disabled:opacity-60 transition-colors duration-300"
        >
          {saving ? "SALVATAGGIO…" : "SALVA PROGETTO"}
        </button>
      </form>
    </div>
  );
};

export default ProjectEditor;
